import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { message } from 'antd'
import { ApiResponse } from '@types'

/** 构建时由 .env.production 注入，默认与站点同源 /api */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api'

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // 请求拦截器
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        // FormData 上传时移除实例默认的 Content-Type，让浏览器自动设置 boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }
        console.log('API请求:', config.method?.toUpperCase(), config.url, config.data)
        return config
      },
      (error) => {
        console.error('请求拦截器错误:', error)
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        if (response.config.responseType === 'blob') {
          return response
        }
        const { data } = response
        console.log('API响应:', response.status, response.config.url, data)
        
        if (!data.success) {
          console.error('API返回失败:', data)
          message.error(data.message || '请求失败')
          return Promise.reject(new Error(data.message || '请求失败'))
        }
        
        return response
      },
      (error) => {
        console.error('API响应错误:', error)
        if (error.response) {
          const { status, data } = error.response
          const reqUrl = String(error.config?.url || '')
          console.error('响应错误详情:', { status, data })
          
          switch (status) {
            case 401:
              // 登录/刷新令牌失败不应当作「已登录态过期」，避免误清 token、误跳转
              if (reqUrl.includes('/auth/login') || reqUrl.includes('/auth/refresh')) {
                break
              }
              message.error('登录已过期，请重新登录')
              localStorage.removeItem('admin_token')
              localStorage.removeItem('admin_refresh_token')
              window.location.href = '/login'
              break
            case 403:
              message.error('没有权限访问该资源')
              break
            case 404:
              message.error('请求的资源不存在')
              break
            case 500:
            case 503:
              message.error(
                (data && typeof data.message === 'string' && data.message) ||
                  '服务器暂时不可用，请稍后重试'
              )
              break
            default:
              message.error(data?.message || '请求失败')
          }
        } else if (error.request) {
          console.error('网络请求失败:', error.request)
          message.error('网络连接失败，请检查网络')
        } else {
          console.error('请求配置错误:', error.message)
          message.error('请求配置错误')
        }
        
        return Promise.reject(error)
      }
    )
  }

  // GET请求
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<ApiResponse<T>>(url, config)
    return response.data.data
  }

  /**
   * GET 并返回完整包体 { success, data, message, total, ... }（带鉴权）。
   * 供从原生 fetch 迁移的页面使用，避免漏带 Authorization 导致 401。
   */
  async getEnvelope<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url, config)
    return response.data
  }

  // POST请求
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<ApiResponse<T>>(url, data, config)
    return response.data.data
  }

  // PUT请求
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<ApiResponse<T>>(url, data, config)
    return response.data.data
  }

  // DELETE请求
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<ApiResponse<T>>(url, config)
    return response.data.data
  }

  // PATCH请求
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<ApiResponse<T>>(url, data, config)
    return response.data.data
  }

  // 文件上传
  async upload<T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<ApiResponse<T>>(url, formData, config)
    return response.data.data
  }

  // 文件下载
  async download(url: string, filename?: string, config?: AxiosRequestConfig): Promise<void> {
    const response = await this.api.get(url, {
      ...config,
      responseType: 'blob',
    })
    
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  /** POST 导出文件（响应为 CSV 等二进制流） */
  async postDownload(
    url: string,
    body: unknown,
    fallbackFilename: string
  ): Promise<void> {
    const response = await this.api.post(url, body, { responseType: 'blob' })
    const blob = response.data as Blob
    const ct = (response.headers['content-type'] || '').toLowerCase()
    if (ct.includes('application/json')) {
      const text = await blob.text()
      const j = JSON.parse(text) as { message?: string }
      throw new Error(j.message || '导出失败')
    }
    const disp = String(response.headers['content-disposition'] || '')
    const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(disp)
    const asciiMatch = /filename="([^"]+)"/i.exec(disp)
    let filename = fallbackFilename
    if (utf8Match?.[1]) {
      filename = decodeURIComponent(utf8Match[1].trim())
    } else if (asciiMatch?.[1]) {
      filename = asciiMatch[1].trim()
    }
    const urlObj = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = urlObj
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(urlObj)
  }
}

export const apiService = new ApiService()



